import { Button, ExternalLink, LoadingBar, Popup } from '@approvalmax/ui';
import { List, useExtensions } from '@approvalmax/ui/src/components';
import { useSelector } from 'modules/react-redux';
import { FC, memo, useCallback, useState } from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { notificationService } from 'services/notification';
import { useImportManualJournal } from 'shared/data/webApp/v1';

import { getRequest } from '../../../../../selectors/pageSelectors';
import ImportFilePreview from './components/ImportFilePreview/ImportFilePreview';
import {
    DOWNLOAD_TEMPLATE_LINK,
    LEARN_MORE_LINK,
    MAX_FILE_SIZE,
    MAX_FILE_SIZE_TEXT,
    rules,
} from './ManualJournalImportPopup.constants';
import { messages } from './ManualJournalImportPopup.messages';
import {
    Content,
    DropZoneContainer,
    DropZoneOverlay,
    FileSection,
    LoadingBarContainer,
    Root,
    Warning,
} from './ManualJournalImportPopup.styles';
import { ManualJournalImportPopupProps } from './ManualJournalImportPopup.types';

const ManualJournalImportPopup: FC<ManualJournalImportPopupProps> = memo((props) => {
    const { onSuccess } = props;

    const { companyId, id: requestId } = useSelector(getRequest);
    const extensions = useExtensions(['csv']);
    const [manualJournalSourceFileName, setManualJournalSourceFileName] = useState<string | null>(null);
    const [manualJournalSourceFile, setManualJournalSourceFile] = useState<File | null>(null);
    const { mutate: importManualJournal, isLoading: isLoadingImportManualJournal } = useImportManualJournal();

    const description = messages.description({
        learnMore: <ExternalLink href={LEARN_MORE_LINK}>{messages.learnMoreText}</ExternalLink>,
        downloadLink: <ExternalLink href={DOWNLOAD_TEMPLATE_LINK}>{messages.downloadLinkText}</ExternalLink>,
        br: <br />,
    });

    const onDrop = useCallback(
        (accepted: File[], rejected: FileRejection[]) => {
            if (manualJournalSourceFileName) {
                return;
            }

            if (accepted.length === 0 && rejected.length === 1) {
                const { file: rejectedFile } = rejected[0];
                const size = rejectedFile.size;

                if (size > MAX_FILE_SIZE) {
                    notificationService.showErrorToast(
                        messages.fileSizeExceedsTheLimit({
                            size: MAX_FILE_SIZE_TEXT,
                        })
                    );
                }
            }

            if (accepted.length !== 1) {
                return;
            }

            const file = accepted[0];

            setManualJournalSourceFile(file);
            setManualJournalSourceFileName(file.name);
        },
        [manualJournalSourceFileName]
    );

    const onRemoveFile = useCallback(() => {
        setManualJournalSourceFile(null);
        setManualJournalSourceFileName(null);
    }, []);

    const importFile = useCallback(() => {
        if (!manualJournalSourceFile) {
            return;
        }

        let formData = new FormData();

        formData.append('companyId', companyId);
        formData.append('requestId', requestId);
        formData.append('file', manualJournalSourceFile);

        importManualJournal(
            { body: formData },
            {
                onSuccess,
                onError: () => {
                    setManualJournalSourceFileName(null);
                },
            }
        );
    }, [companyId, importManualJournal, manualJournalSourceFile, onSuccess, requestId]);

    return (
        <Root
            title={messages.title}
            buttons={
                <Popup.DefaultContent.Button
                    disabled={!manualJournalSourceFile || isLoadingImportManualJournal}
                    execute={importFile}
                >
                    {messages.button}
                </Popup.DefaultContent.Button>
            }
            disableCloseButton={isLoadingImportManualJournal}
        >
            <>
                <LoadingBarContainer>{isLoadingImportManualJournal && <LoadingBar />}</LoadingBarContainer>

                <Warning>{messages.warningText}</Warning>

                <Dropzone
                    maxSize={MAX_FILE_SIZE}
                    multiple={false}
                    accept={extensions}
                    onDrop={onDrop}
                    noClick
                    noKeyboard
                >
                    {({ getRootProps, getInputProps, isDragActive, open }) => (
                        <DropZoneContainer {...getRootProps()}>
                            <input {...getInputProps()} />

                            <Content>
                                {description}

                                <List>
                                    {rules.map((rule) => (
                                        <List.Item key={rule}>&bull; {messages[rule]}</List.Item>
                                    ))}
                                </List>

                                <FileSection>
                                    {!manualJournalSourceFileName ? (
                                        <Button execute={open}>{messages.uploadFileButtonText}</Button>
                                    ) : (
                                        <ImportFilePreview
                                            fileName={manualJournalSourceFileName}
                                            disabled={isLoadingImportManualJournal}
                                            onRemove={onRemoveFile}
                                        />
                                    )}
                                </FileSection>
                            </Content>

                            <DropZoneOverlay $active={isDragActive}>{messages.dropHereText}</DropZoneOverlay>
                        </DropZoneContainer>
                    )}
                </Dropzone>
            </>
        </Root>
    );
});

export default ManualJournalImportPopup;
